import { useEffect, useRef, useState } from 'react';
import { isFailed, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import useFetchReportRemixes from '../../../../../../hooks/useFetchReportRemixes';
const getIsReportChangedAndFetchNotDeferred = (report, prevReportRef, isDeferred) => {
  /**
   * don't reset the prevReportRef if the request is deferred because if isDeferred is true,
   * useAsyncFetcher does not allow dep updates to trigger new requests. Because of this
   * we only want to mark a report as different to trigger changes only when the hook is not
   * deferred
   *  */
  if (prevReportRef.current !== report && !isDeferred) {
    prevReportRef.current = report;
    return true;
  } else {
    return false;
  }
};
const useFetchGenerateReportRemixes = ({
  report,
  deferred: deferredOverride,
  numberOfRemixes = 3,
  disableSameReportCaching
}) => {
  const prevReportRef = useRef();
  const prevRemixFetchResultRef = useRef();
  const isReportChangedAndFetchNotDeferred = getIsReportChangedAndFetchNotDeferred(report, prevReportRef, !!deferredOverride);
  const shouldDeferRequestBecauseReportIsSame = !disableSameReportCaching && !isReportChangedAndFetchNotDeferred && !!prevRemixFetchResultRef.current;
  const [forceRefreshFromFailedFetch, setForceRefreshFromFailedFetch] = useState(false);
  const promptFetch = useFetchReportRemixes({
    report,
    numberOfRemixes,
    deferred: !forceRefreshFromFailedFetch && (deferredOverride || shouldDeferRequestBecauseReportIsSame)
  });
  useEffect(() => {
    if (isFailed(promptFetch)) {
      setForceRefreshFromFailedFetch(true);
    } else {
      setForceRefreshFromFailedFetch(false);
    }
  }, [promptFetch]);
  useEffect(() => {
    if (isSucceeded(promptFetch)) {
      // cache last succeeded result
      prevRemixFetchResultRef.current = promptFetch;
    }
    if (isReportChangedAndFetchNotDeferred) {
      // if report changes, clear the cache. This is because the insights are not valid for the current report anymore
      prevRemixFetchResultRef.current = undefined;
    }
  }, [isReportChangedAndFetchNotDeferred, promptFetch]);
  const shouldReturnCachedRemixResultIfReportIsUnchanged = !disableSameReportCaching && !isReportChangedAndFetchNotDeferred && !!prevRemixFetchResultRef.current;
  if (shouldReturnCachedRemixResultIfReportIsUnchanged &&
  // duplicating this check to make it explicit to TS that this is defined to avoid err
  !!prevRemixFetchResultRef.current) {
    return prevRemixFetchResultRef.current;
  }
  return promptFetch;
};
export default useFetchGenerateReportRemixes;